import React, { useState } from 'react';
import {
  Cloud,
  Server,
  ShieldCheck,
  Cpu,
  GitBranch,
  Terminal,
  Activity,
  Layers,
  CheckCircle2,
  ArrowRight,
  Workflow,
  BarChart3,
  Sparkles,
  Search,
  Globe,
  Boxes,
  RefreshCw,
  Settings,
  HardDrive,
  Headphones
} from 'lucide-react';
import { Button } from '../components/ui/button';

const CloudDevOps = () => {
  const [activeCloud, setActiveCloud] = useState('aws');

  const devopsPillars = [
    {
      icon: GitBranch,
      title: 'Automated CI/CD Pipelines',
      badge: 'Zero Downtime',
      color: 'from-cyan-500 to-blue-600',
      badgeBg: 'bg-cyan-100 text-cyan-700 border-cyan-200',
      description: 'Robust GitHub Actions, GitLab CI, and Jenkins build pipelines featuring blue/green deployments, automated canary releases, and instant rollback triggers.',
      bullets: ['GitHub Actions & GitLab CI/CD', 'Blue/Green & Canary Deployments', 'Automated Regression & Rollback']
    },
    {
      icon: Cpu,
      title: 'Kubernetes & Docker Orchestration',
      badge: 'Auto-Scaling',
      color: 'from-purple-500 to-indigo-600',
      badgeBg: 'bg-purple-100 text-purple-700 border-purple-200',
      description: 'Enterprise Kubernetes cluster setups (AWS EKS, GCP GKE) with automated pod horizontal scaling, load balancing, and self-healing node clusters.',
      bullets: ['AWS EKS & GCP GKE Clusters', 'Horizontal Pod Autoscaler (HPA)', 'Self-Healing Container Pods']
    },
    {
      icon: Workflow,
      title: 'Infrastructure as Code (Terraform)',
      badge: 'Immutable Infra',
      color: 'from-orange-500 to-amber-500',
      badgeBg: 'bg-amber-100 text-amber-800 border-amber-200',
      description: 'Declarative infrastructure management using Terraform and Ansible to ensure repeatable, version-controlled cloud environments with zero manual drift.',
      bullets: ['Terraform Cloud & HCL Modules', 'Ansible Configuration Drift Control', 'Multi-Environment Staging/Prod Sync']
    },
    {
      icon: Cloud,
      title: 'Multi-Cloud Architecture (AWS / GCP)',
      badge: 'Multi-Region',
      color: 'from-emerald-500 to-teal-600',
      badgeBg: 'bg-emerald-100 text-emerald-700 border-emerald-200',
      description: 'High-availability multi-cloud designs utilizing AWS, Google Cloud, and Azure with global CDN caching, DNS failover, and serverless edge functions.',
      bullets: ['AWS / GCP Multi-Region Failover', 'Serverless AWS Lambda & Cloud Run', 'Cloudflare Enterprise CDN & WAF']
    },
    {
      icon: ShieldCheck,
      title: 'DevSecOps & Zero-Trust Security',
      badge: 'Bank Grade',
      color: 'from-rose-500 to-pink-600',
      badgeBg: 'bg-rose-100 text-rose-700 border-rose-200',
      description: 'Continuous container vulnerability scanning (Trivy), HashiCorp Vault secrets management, IAM Role-Based Access Control, and automated TLS 1.3 renewal.',
      bullets: ['HashiCorp Vault Secret Management', 'Trivy Container Vulnerability Scan', 'IAM Granular RBAC Permissions']
    },
    {
      icon: BarChart3,
      title: 'Real-Time Observability & Monitoring',
      badge: 'Full Telemetry',
      color: 'from-blue-500 to-cyan-600',
      badgeBg: 'bg-blue-100 text-blue-700 border-blue-200',
      description: 'Centralized log aggregation and metric tracking with Prometheus, Grafana, ELK Stack, Datadog, and automated PagerDuty emergency alerts.',
      bullets: ['Prometheus & Grafana Dashboards', 'ELK Stack & Datadog APM Tracing', 'PagerDuty Incident Escalation']
    }
  ];

  const cloudSpecs = {
    aws: {
      name: 'Amazon Web Services (AWS)',
      desc: 'Industry-standard cloud infrastructure offering high scalability, EKS Kubernetes, RDS PostgreSQL DB clusters, and S3 global storage.',
      specs: ['AWS EKS Kubernetes Clusters', 'AWS Aurora PostgreSQL Multi-AZ', 'AWS CloudFront Global CDN', 'AWS Lambda Serverless Engine']
    },
    gcp: {
      name: 'Google Cloud Platform (GCP)',
      desc: 'Optimized for high-performance Kubernetes (GKE), BigQuery AI analytics data warehouses, and serverless Cloud Run applications.',
      specs: ['Google Kubernetes Engine (GKE)', 'BigQuery AI & Analytics Warehouse', 'GCP Cloud Run Serverless', 'Cloud Spanner Global Database']
    },
    k8s: {
      name: 'Kubernetes & Docker Container Stack',
      desc: 'Microservice containerization with Helm charts, Istio Service Mesh, and automated GitOps continuous deployment using ArgoCD.',
      specs: ['ArgoCD GitOps Deployment', 'Istio Service Mesh Traffic Control', 'Helm Chart Package Specs', 'Docker Multi-Stage Build Specs']
    }
  };

  const roadmapSteps = [
    {
      step: '01',
      title: 'Cloud Assessment & Strategy',
      description: 'Evaluate existing infrastructure, applications, business goals, security requirements, and cloud readiness to define the optimal cloud adoption strategy.',
      icon: Search
    },
    {
      step: '02',
      title: 'Cloud Architecture & Infrastructure Design',
      description: 'Design secure, scalable, and cost-efficient cloud architecture, including networking, storage, virtual machines, containers, security, and disaster recovery.',
      icon: Layers
    },
    {
      step: '03',
      title: 'Infrastructure Automation (IaC)',
      description: 'Automate infrastructure provisioning using Infrastructure as Code (IaC) to ensure consistent, repeatable, and scalable deployments.',
      icon: Workflow
    },
    {
      step: '04',
      title: 'CI/CD Pipeline Implementation',
      description: 'Build automated Continuous Integration and Continuous Deployment pipelines to accelerate software delivery, reduce manual effort, and improve release quality.',
      icon: GitBranch
    },
    {
      step: '05',
      title: 'Monitoring, Security & Performance Optimization',
      description: 'Implement centralized monitoring, logging, cloud security, backup strategies, performance tuning, cost optimization, and automated alerting.',
      icon: ShieldCheck
    },
    {
      step: '06',
      title: 'Managed DevOps & Continuous Support',
      description: 'Provide ongoing infrastructure management, scaling, cloud optimization, security updates, maintenance, and 24/7 operational support.',
      icon: Server
    }
  ];

  const techStack = [
    { name: 'AWS', category: 'Cloud Platform', icon: Cloud, color: 'text-amber-500', iconBg: 'bg-amber-500/10 border border-amber-500/20' },
    { name: 'Microsoft Azure', category: 'Enterprise Cloud', icon: Globe, color: 'text-blue-500', iconBg: 'bg-blue-500/10 border border-blue-500/20' },
    { name: 'Google Cloud Platform (GCP)', category: 'Cloud Compute', icon: Cpu, color: 'text-cyan-500', iconBg: 'bg-cyan-500/10 border border-cyan-500/20' },
    { name: 'Docker', category: 'Container Engine', icon: Boxes, color: 'text-sky-500', iconBg: 'bg-sky-500/10 border border-sky-500/20' },
    { name: 'Kubernetes', category: 'Container Orchestration', icon: Layers, color: 'text-indigo-600', iconBg: 'bg-indigo-500/10 border border-indigo-500/20' },
    { name: 'Terraform', category: 'Infrastructure as Code', icon: Workflow, color: 'text-purple-600', iconBg: 'bg-purple-500/10 border border-purple-500/20' },
    { name: 'Jenkins', category: 'Automation Server', icon: RefreshCw, color: 'text-rose-500', iconBg: 'bg-rose-500/10 border border-rose-500/20' },
    { name: 'GitHub Actions', category: 'CI/CD Automation', icon: GitBranch, color: 'text-emerald-500', iconBg: 'bg-emerald-500/10 border border-emerald-500/20' },
    { name: 'GitLab CI/CD', category: 'DevOps Engine', icon: Terminal, color: 'text-orange-600', iconBg: 'bg-orange-500/10 border border-orange-500/20' },
    { name: 'Ansible', category: 'Config Management', icon: Settings, color: 'text-[#0B1F3A]', iconBg: 'bg-slate-200 border border-slate-300' },
    { name: 'Linux', category: 'Enterprise OS', icon: HardDrive, color: 'text-yellow-600', iconBg: 'bg-yellow-500/10 border border-yellow-500/20' },
    { name: 'NGINX', category: 'Web Server & Proxy', icon: Server, color: 'text-teal-600', iconBg: 'bg-teal-500/10 border border-teal-500/20' },
    { name: 'Prometheus', category: 'Metrics & Telemetry', icon: Activity, color: 'text-orange-500', iconBg: 'bg-orange-500/10 border border-orange-500/20' },
    { name: 'Grafana', category: 'Observability Dashboards', icon: BarChart3, color: 'text-pink-500', iconBg: 'bg-pink-500/10 border border-pink-500/20' }
  ];

  return (
    <div className="min-h-screen bg-white text-left pt-20 text-[#0B1F3A] font-sans selection:bg-[#D4AF37] selection:text-[#0B1F3A]">

      {/* Hero Showcase Section */}
      <section className="py-24 bg-gray-950 relative overflow-hidden border-b border-gray-800 text-white">
        {/* Dark Background Image */}
        <img
          src="/images/enterprise_cloud_hero_dark.png"
          alt="Enterprise Cloud Infrastructure Background"
          className="absolute inset-0 w-full h-full object-cover object-center opacity-50 z-0 pointer-events-none"
          onError={(e) => {
            e.target.onerror = null;
            e.target.src = "https://images.unsplash.com/photo-1558494949-ef010cbdcc31?w=1600&q=80";
          }}
        />

        {/* Dark Gradient Overlay for High Contrast */}
        <div className="absolute inset-0 z-0 bg-gradient-to-r from-gray-950/85 via-[#0B1F3A]/70 to-gray-950/85 pointer-events-none" />

        <div className="container mx-auto px-6 relative z-10">
          <div className="grid lg:grid-cols-12 gap-12 items-center">

            {/* Left Copy */}
            <div className="lg:col-span-7">
              <div className="inline-flex items-center space-x-2 bg-[#D4AF37]/10 border border-[#D4AF37]/30 rounded-full px-4 py-1.5 mb-6">
                <Cloud className="w-4 h-4 text-[#D4AF37]" />
                <span className="text-[#D4AF37] text-sm font-semibold uppercase tracking-wider">
                  HOASP DevOps & Cloud Engineering
                </span>
              </div>

              <h1 className="text-4xl md:text-6xl font-extrabold text-white mb-6 leading-tight tracking-tight">
                Automated CI/CD & <span className="bg-gradient-to-r from-[#D4AF37] via-amber-300 to-cyan-400 bg-clip-text text-transparent">Cloud Architecture</span>
              </h1>

              <p className="text-gray-300 text-base md:text-xl leading-relaxed mb-8">
                Build high-availability AWS, GCP, and Azure cloud infrastructure with Kubernetes container orchestration, Terraform Infrastructure as Code, and zero-downtime CI/CD deployment pipelines.
              </p>

              <div className="flex flex-wrap gap-4">
                <a href="/contact">
                  <Button className="bg-[#D4AF37] hover:bg-[#c29f2e] text-[#0B1F3A] px-8 py-6 text-base font-extrabold rounded-xl shadow-lg hover:shadow-[#D4AF37]/20 transition-all duration-300 flex items-center gap-2">
                    Consult Cloud Architects
                    <ArrowRight className="w-5 h-5" />
                  </Button>
                </a>
                <a href="#roadmap">
                  <Button variant="outline" className="border-gray-700 text-gray-200 hover:bg-white/10 hover:text-white px-8 py-6 text-base font-semibold rounded-xl">
                    View Implementation Roadmap
                  </Button>
                </a>
              </div>
            </div>

            {/* Right Side - Automated Cloud Architecture & CI/CD Pipeline Motion Diagram */}
            <div className="lg:col-span-5 relative">
              <div className="bg-gray-900/95 border border-[#D4AF37]/40 rounded-3xl p-6 shadow-2xl relative overflow-hidden backdrop-blur-xl">

                {/* Diagram Header */}
                <div className="flex items-center justify-between pb-4 border-b border-gray-800 mb-5">
                  <div className="flex items-center space-x-2">
                    <div className="w-2.5 h-2.5 rounded-full bg-emerald-400 animate-ping" />
                    <span className="text-xs font-mono text-emerald-400 font-bold uppercase tracking-wider">
                      AUTOMATED PIPELINE PROCESS
                    </span>
                  </div>
                  <span className="text-[11px] font-mono text-[#D4AF37] bg-[#D4AF37]/10 px-2.5 py-1 rounded-full border border-[#D4AF37]/30">
                    AWS EKS / TERRAFORM / GITOPS
                  </span>
                </div>

                {/* 5-Step Process Pipeline Flow */}
                <div className="space-y-3 relative">

                  {/* Stage 1: Source & Code Commit */}
                  <div className="bg-gray-950/90 border border-gray-800 hover:border-cyan-500/60 rounded-2xl p-3.5 flex items-center justify-between transition-all group">
                    <div className="flex items-center space-x-3">
                      <div className="w-9 h-9 rounded-xl bg-cyan-500/10 border border-cyan-500/30 text-cyan-400 flex items-center justify-center flex-shrink-0 shadow-sm group-hover:scale-110 transition-transform">
                        <GitBranch className="w-4 h-4" />
                      </div>
                      <div>
                        <div className="text-[10px] font-mono text-cyan-400 font-semibold uppercase">01. CODE & COMMIT</div>
                        <div className="text-xs font-bold text-white">GitHub Actions / GitLab Webhook</div>
                      </div>
                    </div>
                    <span className="text-[10px] font-mono text-cyan-300 bg-cyan-500/10 px-2 py-0.5 rounded border border-cyan-500/20">
                      PUSH
                    </span>
                  </div>

                  {/* Stage 2: Automated Build & Test */}
                  <div className="bg-gray-950/90 border border-gray-800 hover:border-[#D4AF37]/60 rounded-2xl p-3.5 flex items-center justify-between transition-all group">
                    <div className="flex items-center space-x-3">
                      <div className="w-9 h-9 rounded-xl bg-[#D4AF37]/10 border border-[#D4AF37]/30 text-[#D4AF37] flex items-center justify-center flex-shrink-0 shadow-sm group-hover:scale-110 transition-transform">
                        <Workflow className="w-4 h-4" />
                      </div>
                      <div>
                        <div className="text-[10px] font-mono text-[#D4AF37] font-semibold uppercase">02. BUILD & TEST</div>
                        <div className="text-xs font-bold text-white">Docker Build & Security Scan</div>
                      </div>
                    </div>
                    <span className="text-[10px] font-mono text-[#D4AF37] bg-[#D4AF37]/10 px-2 py-0.5 rounded border border-[#D4AF37]/20">
                      PASSED
                    </span>
                  </div>

                  {/* Stage 3: IaC Provisioning */}
                  <div className="bg-gray-950/90 border border-gray-800 hover:border-amber-500/60 rounded-2xl p-3.5 flex items-center justify-between transition-all group">
                    <div className="flex items-center space-x-3">
                      <div className="w-9 h-9 rounded-xl bg-amber-500/10 border border-amber-500/30 text-amber-400 flex items-center justify-center flex-shrink-0 shadow-sm group-hover:scale-110 transition-transform">
                        <Terminal className="w-4 h-4" />
                      </div>
                      <div>
                        <div className="text-[10px] font-mono text-amber-400 font-semibold uppercase">03. IaC PROVISIONING</div>
                        <div className="text-xs font-bold text-white">Terraform Infrastructure Apply</div>
                      </div>
                    </div>
                    <span className="text-[10px] font-mono text-amber-300 bg-amber-500/10 px-2 py-0.5 rounded border border-amber-500/20">
                      APPLIED
                    </span>
                  </div>

                  {/* Stage 4: Kubernetes Orchestration */}
                  <div className="bg-gray-950/90 border border-gray-800 hover:border-purple-500/60 rounded-2xl p-3.5 flex items-center justify-between transition-all group">
                    <div className="flex items-center space-x-3">
                      <div className="w-9 h-9 rounded-xl bg-purple-500/10 border border-purple-500/30 text-purple-400 flex items-center justify-center flex-shrink-0 shadow-sm group-hover:scale-110 transition-transform">
                        <Cpu className="w-4 h-4" />
                      </div>
                      <div>
                        <div className="text-[10px] font-mono text-purple-400 font-semibold uppercase">04. K8S DEPLOYMENT</div>
                        <div className="text-xs font-bold text-white">AWS EKS Cluster Auto-Scaling</div>
                      </div>
                    </div>
                    <span className="text-[10px] font-mono text-purple-300 bg-purple-500/10 px-2 py-0.5 rounded border border-purple-500/20">
                      64 PODS
                    </span>
                  </div>

                  {/* Stage 5: Telemetry & Monitoring */}
                  <div className="bg-gray-950/90 border border-gray-800 hover:border-emerald-500/60 rounded-2xl p-3.5 flex items-center justify-between transition-all group">
                    <div className="flex items-center space-x-3">
                      <div className="w-9 h-9 rounded-xl bg-emerald-500/10 border border-emerald-500/30 text-emerald-400 flex items-center justify-center flex-shrink-0 shadow-sm group-hover:scale-110 transition-transform">
                        <Activity className="w-4 h-4" />
                      </div>
                      <div>
                        <div className="text-[10px] font-mono text-emerald-400 font-semibold uppercase">05. MONITORING & SLA</div>
                        <div className="text-xs font-bold text-white">Prometheus & Grafana Telemetry</div>
                      </div>
                    </div>
                    <span className="text-[10px] font-mono text-emerald-400 bg-emerald-500/10 px-2 py-0.5 rounded border border-emerald-500/20">
                      99.999% SLA
                    </span>
                  </div>

                </div>

                {/* Diagram Footer Status Bar */}
                <div className="mt-5 pt-3 border-t border-gray-800 flex items-center justify-between font-mono text-[11px] text-gray-400">
                  <div className="flex items-center space-x-2">
                    <CheckCircle2 className="w-3.5 h-3.5 text-emerald-400" />
                    <span>STATUS: 0 DOWNTIME</span>
                  </div>
                  <span className="text-[#D4AF37] font-bold">LATENCY &lt; 2 MIN</span>
                </div>

              </div>
            </div>

          </div>
        </div>

        {/* Ambient Glow */}
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[600px] h-[600px] bg-[#D4AF37]/10 rounded-full blur-3xl pointer-events-none" />
      </section>

      {/* Cloud SLA Performance Banner */}
      <section className="py-12 bg-slate-50 border-b border-slate-200">
        <div className="container mx-auto px-6">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-6">

            <div className="bg-white border border-slate-200 rounded-2xl p-6 shadow-sm hover:shadow-md transition-shadow relative overflow-hidden">
              <div className="h-1.5 w-full bg-[#0B1F3A] absolute top-0 left-0" />
              <div className="text-3xl md:text-4xl font-black text-[#0B1F3A] mb-1">99.999%</div>
              <div className="text-xs md:text-sm text-slate-600 font-bold uppercase tracking-wider">Infrastructure Uptime SLA</div>
            </div>

            <div className="bg-white border border-slate-200 rounded-2xl p-6 shadow-sm hover:shadow-md transition-shadow relative overflow-hidden">
              <div className="h-1.5 w-full bg-[#D4AF37] absolute top-0 left-0" />
              <div className="text-3xl md:text-4xl font-black text-[#0B1F3A] mb-1">&lt; 2 Min</div>
              <div className="text-xs md:text-sm text-slate-600 font-bold uppercase tracking-wider">Zero-Downtime Release</div>
            </div>

            <div className="bg-white border border-slate-200 rounded-2xl p-6 shadow-sm hover:shadow-md transition-shadow relative overflow-hidden">
              <div className="h-1.5 w-full bg-[#0B1F3A] absolute top-0 left-0" />
              <div className="text-3xl md:text-4xl font-black text-[#0B1F3A] mb-1">100%</div>
              <div className="text-xs md:text-sm text-slate-600 font-bold uppercase tracking-wider">Terraform Code Coverage</div>
            </div>

            <div className="bg-white border border-slate-200 rounded-2xl p-6 shadow-sm hover:shadow-md transition-shadow relative overflow-hidden">
              <div className="h-1.5 w-full bg-[#D4AF37] absolute top-0 left-0" />
              <div className="text-3xl md:text-4xl font-black text-[#0B1F3A] mb-1">24/7</div>
              <div className="text-xs md:text-sm text-slate-600 font-bold uppercase tracking-wider">Grafana & Alerting</div>
            </div>

          </div>
        </div>
      </section>

      {/* Pillars Grid */}
      <section id="devops-pillars" className="py-24 bg-black border-b border-slate-200">
        <div className="container mx-auto px-6">

          <div className="text-center mb-16 max-w-3xl mx-auto">
            <div className="inline-block bg-[#0B1F3A]/5 border border-[#fff] rounded-full px-4 py-1.5 mb-4">
              <span className="text-[#fff] text-xs md:text-sm font-bold uppercase tracking-wider flex items-center gap-1.5">
                <Sparkles className="w-4 h-4 text-[#fff] inline" />
                Full-Lifecycle DevOps & Cloud Core
              </span>
            </div>
            <h2 className="text-3xl md:text-5xl font-extrabold text-[#fff] mb-4 leading-tight">
              Enterprise Cloud & <span className="text-[#D4AF37]">CI/CD Pillars</span>
            </h2>
            <p className="text-white text-base md:text-lg leading-relaxed">
              From Infrastructure as Code to Kubernetes auto-scaling, blue/green deployments, and 24/7 telemetry monitoring.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {devopsPillars.map((pillar, idx) => (
              <div
                key={idx}
                className="group bg-white border border-slate-200 rounded-2xl p-8 transition-all duration-300 hover:scale-[1.02] hover:shadow-xl hover:border-[#D4AF37] flex flex-col justify-between shadow-sm relative overflow-hidden"
              >
                <div className="h-1.5 w-full bg-[#0B1F3A] group-hover:bg-[#D4AF37] transition-colors absolute top-0 left-0" />
                <div>
                  <div className="flex items-center justify-between mb-6">
                    <div className="w-14 h-14 bg-[#0B1F3A] text-[#D4AF37] rounded-2xl flex items-center justify-center group-hover:scale-110 transition-transform duration-300 shadow-md">
                      <pillar.icon className="w-7 h-7" />
                    </div>
                    <span className={`text-[11px] font-extrabold uppercase tracking-wider px-3 py-1 rounded-full border ${pillar.badgeBg}`}>
                      {pillar.badge}
                    </span>
                  </div>

                  <h3 className="text-xl font-bold text-[#0B1F3A] mb-3 group-hover:text-[#0B1F3A] transition-colors">
                    {pillar.title}
                  </h3>

                  <p className="text-slate-600 text-sm leading-relaxed mb-6">
                    {pillar.description}
                  </p>
                </div>

                <div className="pt-4 border-t border-slate-200 space-y-2.5">
                  {pillar.bullets.map((bullet, i) => (
                    <div key={i} className="flex items-center text-sm text-slate-700 font-semibold">
                      <CheckCircle2 className="w-4 h-4 text-[#D4AF37] mr-2.5 flex-shrink-0" />
                      <span>{bullet}</span>
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>

        </div>
      </section>

      {/* Interactive Cloud Provider & Tool Selector */}
      <section className="py-20 bg-slate-50 border-b border-slate-200">
        <div className="container mx-auto px-6 max-w-5xl">

          <div className="text-center mb-12">
            <h2 className="text-3xl font-extrabold text-[#0B1F3A] mb-4">
              Supported Cloud Platforms & Container Stacks
            </h2>
            <p className="text-slate-600 text-base max-w-xl mx-auto mb-8">
              We design cloud-agnostic architectures or optimize dedicated AWS and Google Cloud environments.
            </p>

            <div className="inline-flex p-1.5 bg-white border border-slate-200 rounded-2xl shadow-sm space-x-2">
              <button
                onClick={() => setActiveCloud('aws')}
                className={`px-5 py-2.5 rounded-xl text-sm font-bold transition-all ${activeCloud === 'aws'
                  ? 'bg-[#0B1F3A] text-white shadow-md'
                  : 'text-slate-600 hover:text-[#0B1F3A] hover:bg-slate-100'
                  }`}
              >
                Amazon Web Services (AWS)
              </button>
              <button
                onClick={() => setActiveCloud('gcp')}
                className={`px-5 py-2.5 rounded-xl text-sm font-bold transition-all ${activeCloud === 'gcp'
                  ? 'bg-[#0B1F3A] text-white shadow-md'
                  : 'text-slate-600 hover:text-[#0B1F3A] hover:bg-slate-100'
                  }`}
              >
                Google Cloud (GCP)
              </button>
              <button
                onClick={() => setActiveCloud('k8s')}
                className={`px-5 py-2.5 rounded-xl text-sm font-bold transition-all ${activeCloud === 'k8s'
                  ? 'bg-[#0B1F3A] text-white shadow-md'
                  : 'text-slate-600 hover:text-[#0B1F3A] hover:bg-slate-100'
                  }`}
              >
                Kubernetes & Docker
              </button>
            </div>
          </div>

          <div className="bg-white border border-slate-200 rounded-3xl p-8 md:p-12 shadow-xl">
            <h3 className="text-2xl font-bold text-[#0B1F3A] mb-3">
              {cloudSpecs[activeCloud].name}
            </h3>
            <p className="text-slate-600 text-base leading-relaxed mb-8">
              {cloudSpecs[activeCloud].desc}
            </p>

            <div className="grid md:grid-cols-2 gap-4">
              {cloudSpecs[activeCloud].specs.map((spec, idx) => (
                <div key={idx} className="bg-slate-50 border border-slate-200 rounded-xl p-4 flex items-center space-x-3 shadow-sm">
                  <CheckCircle2 className="w-5 h-5 text-[#D4AF37] flex-shrink-0" />
                  <span className="text-sm font-bold text-[#0B1F3A]">{spec}</span>
                </div>
              ))}
            </div>
          </div>

        </div>
      </section>

      {/* Enterprise Cloud & DevOps Implementation Roadmap */}
      <section id="roadmap" aria-labelledby="roadmap-heading" className="py-24 bg-black border-b border-gray-800 text-white">
        <div className="container mx-auto px-6 max-w-7xl">

          {/* Section Header */}
          <div className="text-center mb-16 max-w-3xl mx-auto">
            <div className="inline-flex items-center space-x-2 bg-white/10 border border-[#D4AF37]/50 rounded-full px-4 py-1.5 mb-4">
              <Sparkles className="w-4 h-4 text-[#D4AF37]" />
              <span className="text-[#D4AF37] text-xs md:text-sm font-extrabold uppercase tracking-widest">
                Enterprise Execution Methodology
              </span>
            </div>
            <h2 id="roadmap-heading" className="text-3xl md:text-5xl font-black text-white mb-4 leading-tight">
              Cloud & DevOps <span className="text-[#D4AF37]">Implementation Roadmap</span>
            </h2>
            <p className="text-gray-300 text-base md:text-lg leading-relaxed font-medium">
              From cloud strategy and infrastructure automation to CI/CD pipelines, monitoring, and continuous optimization, we help businesses build secure, scalable, and high-availability cloud environments.
            </p>
          </div>

          {/* Desktop Responsive Grid (6 Steps) */}
          <div className="hidden lg:grid grid-cols-3 gap-8 relative">

            {/* Step 1 to 3 Row */}
            {roadmapSteps.slice(0, 3).map((item, idx) => (
              <article
                key={idx}
                className="group bg-gray-900/90 border border-gray-800 rounded-2xl p-8 shadow-xl hover:shadow-2xl hover:border-[#D4AF37] transition-all duration-300 relative overflow-hidden backdrop-blur-md flex flex-col justify-between"
              >
                <div className="h-1.5 w-full bg-[#D4AF37] absolute top-0 left-0" />

                <div>
                  <div className="flex items-center justify-between mb-6">
                    <span className="text-3xl md:text-4xl font-extrabold font-mono text-[#D4AF37]">
                      {item.step}
                    </span>
                    <div className="w-12 h-12 rounded-xl bg-gray-800 text-[#D4AF37] border border-gray-700 flex items-center justify-center shadow-md group-hover:scale-110 transition-transform">
                      <item.icon className="w-6 h-6" />
                    </div>
                  </div>

                  <h3 className="text-xl font-bold text-white mb-3 leading-snug">
                    {item.title}
                  </h3>

                  <p className="text-gray-300 text-sm leading-relaxed font-normal">
                    {item.description}
                  </p>
                </div>
              </article>
            ))}

            {/* Step 4 to 6 Row */}
            {roadmapSteps.slice(3, 6).map((item, idx) => (
              <article
                key={idx + 3}
                className="group bg-gray-900/90 border border-gray-800 rounded-2xl p-8 shadow-xl hover:shadow-2xl hover:border-[#D4AF37] transition-all duration-300 relative overflow-hidden backdrop-blur-md flex flex-col justify-between"
              >
                <div className="h-1.5 w-full bg-[#D4AF37] absolute top-0 left-0" />

                <div>
                  <div className="flex items-center justify-between mb-6">
                    <span className="text-3xl md:text-4xl font-extrabold font-mono text-[#D4AF37]">
                      {item.step}
                    </span>
                    <div className="w-12 h-12 rounded-xl bg-gray-800 text-[#D4AF37] border border-gray-700 flex items-center justify-center shadow-md group-hover:scale-110 transition-transform">
                      <item.icon className="w-6 h-6" />
                    </div>
                  </div>

                  <h3 className="text-xl font-bold text-white mb-3 leading-snug">
                    {item.title}
                  </h3>

                  <p className="text-gray-300 text-sm leading-relaxed font-normal">
                    {item.description}
                  </p>
                </div>
              </article>
            ))}

          </div>

          {/* Mobile & Tablet Vertical Timeline Layout */}
          <div className="lg:hidden relative border-l-2 border-gray-800 ml-4 pl-6 space-y-8">
            {roadmapSteps.map((item, idx) => (
              <article
                key={idx}
                className="group bg-gray-900/90 border border-gray-800 rounded-2xl p-6 shadow-xl hover:shadow-2xl hover:border-[#D4AF37] transition-all relative overflow-hidden"
              >
                <div className="h-1.5 w-full bg-[#D4AF37] absolute top-0 left-0" />

                {/* Left Dot Indicator */}
                <div className="absolute -left-[35px] top-6 w-5 h-5 rounded-full bg-[#D4AF37] border-4 border-black shadow-md text-[#D4AF37]" />

                <div className="flex items-center justify-between mb-4">
                  <span className="text-3xl font-black font-mono text-[#D4AF37]">
                    {item.step}
                  </span>
                  <div className="w-10 h-10 rounded-xl bg-gray-800 text-[#D4AF37] border border-gray-700 flex items-center justify-center shadow-md">
                    <item.icon className="w-5 h-5" />
                  </div>
                </div>

                <h3 className="text-lg font-bold text-white mb-2 leading-snug">
                  {item.title}
                </h3>

                <p className="text-gray-300 text-sm leading-relaxed font-normal">
                  {item.description}
                </p>
              </article>
            ))}
          </div>

        </div>
      </section>

      {/* Supported Technology Stack Section */}
      <section aria-labelledby="tech-stack-heading" className="py-24 bg-slate-50 border-b border-slate-200">
        <div className="container mx-auto px-6 max-w-7xl">

          <div className="text-center mb-16 max-w-2xl mx-auto">
            <h2 id="tech-stack-heading" className="text-3xl md:text-4xl font-extrabold text-[#0B1F3A] mb-3">
              Enterprise Cloud & DevOps Technology Stack
            </h2>
            <p className="text-slate-600 text-base leading-relaxed font-medium">
              Enterprise-grade technology stack engineered across high-availability cloud platforms, container orchestrators, IaC tooling, and monitoring engines.
            </p>
          </div>

          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-7 gap-4">
            {techStack.map((tech, idx) => (
              <div
                key={idx}
                className="bg-white border border-slate-200/90 rounded-2xl p-5 flex flex-col items-center justify-center text-center shadow-sm hover:shadow-xl hover:border-[#D4AF37] hover:scale-105 group transition-all duration-300 relative overflow-hidden"
              >
                <div className={`w-12 h-12 rounded-2xl flex items-center justify-center mb-3 group-hover:scale-110 transition-transform shadow-sm ${tech.iconBg}`}>
                  <tech.icon className={`w-6 h-6 ${tech.color}`} />
                </div>
                <span className="text-xs font-extrabold text-[#0B1F3A] transition-colors leading-tight">
                  {tech.name}
                </span>
                <span className="text-[10px] text-slate-500 font-semibold transition-colors mt-1">
                  {tech.category}
                </span>
              </div>
            ))}
          </div>

        </div>
      </section>

      {/* Enterprise Executive CTA Section */}
      <section aria-labelledby="cta-heading" className="py-24 bg-black text-white relative overflow-hidden border-t-4 border-[#D4AF37]">
        <div className="container mx-auto px-6 relative z-10 max-w-4xl text-center">
          <div className="bg-white border border-slate-200/90 rounded-3xl p-10 md:p-16 shadow-2xl relative z-10 text-[#0B1F3A]">

            <h2 id="cta-heading" className="text-3xl md:text-4xl font-extrabold text-[#0B1F3A] mb-4 leading-tight">
              Ready to Modernize Your Cloud Infrastructure?
            </h2>

            <p className="text-slate-600 text-base md:text-lg mb-8 max-w-2xl mx-auto leading-relaxed font-medium">
              Accelerate your digital transformation with secure cloud architecture, DevOps automation, CI/CD pipelines, infrastructure monitoring, and continuous optimization tailored to your business needs.
            </p>

            <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
              <a href="/contact" className="w-full sm:w-auto">
                <Button className="w-full bg-gradient-to-r from-orange-500 via-amber-500 to-orange-600 hover:from-orange-600 hover:to-amber-600 text-white font-extrabold px-8 py-6 text-base rounded-xl shadow-lg shadow-orange-500/25 hover:shadow-orange-500/40 hover:scale-105 transition-all duration-300 flex items-center justify-center gap-2 border-none">
                  Get a Free Cloud Consultation
                  <ArrowRight className="w-5 h-5 text-white" />
                </Button>
              </a>
              <a href="/contact" className="w-full sm:w-auto">
                <Button className="w-full bg-gradient-to-r from-cyan-600 via-blue-600 to-indigo-600 hover:from-cyan-700 hover:to-indigo-700 text-white font-extrabold px-8 py-6 text-base rounded-xl shadow-lg shadow-cyan-500/25 hover:shadow-cyan-500/40 hover:scale-105 transition-all duration-300 flex items-center justify-center gap-2 border-none">
                  Talk to a DevOps Expert
                  <Headphones className="w-5 h-5 text-white" />
                </Button>
              </a>
            </div>

          </div>
        </div>

        {/* Subtle Background Glow */}
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[700px] h-[700px] bg-[#D4AF37]/10 rounded-full blur-3xl pointer-events-none" />
      </section>

    </div>
  );
};

export default CloudDevOps;
